import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, Hash, KeyRound, Mail, MailCheck } from 'lucide-react';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import type { z } from 'zod';

import {
  AuthError,
  AuthField,
  AuthHeading,
  authLinkClass,
  AuthPasswordField,
  AuthSubmit,
  PasswordChecklist,
  recoverConfirmSchema,
  recoverEmailSchema,
  useAuth,
  useAuthNavigation,
  type AuthSession,
} from '@/app/auth';
import { FlowPanelTrail } from '@/shared/flow';

export const Route = createFileRoute('/login/recuperar-senha')({
  component: RecoverPasswordScreen,
});

/** Recuperação em dois passos: e-mail → código + senha nova, e já entra (paridade com o legado). */
function RecoverPasswordScreen() {
  const { session, codeSent, email, pending, error } = useAuth();
  const second = Boolean(codeSent && email);

  useEffect(() => {
    session.clearError();
  }, [session]);

  return (
    <div className="flex flex-col gap-7">
      <AuthHeading
        icon={second ? MailCheck : KeyRound}
        eyebrow={second ? <Trans>Passo 2 de 2</Trans> : <Trans>Passo 1 de 2</Trans>}
        title={<Trans>Recuperar senha</Trans>}
      >
        {second ? (
          <Trans>Um código foi enviado por e-mail. Digite no campo abaixo com a nova senha para concluir.</Trans>
        ) : (
          <Trans>Um código será enviado por e-mail para você cadastrar uma nova senha.</Trans>
        )}
      </AuthHeading>
      <RecoverTrail second={second} />
      <AuthError message={error} />
      {second && email ? <ConfirmStep key="confirm" session={session} email={email} pending={pending} /> : <EmailStep key="email" session={session} pending={pending} />}
    </div>
  );
}

/** O mini-trilho do Fluxo guiado (U53, discovery/13 família B) no lugar das duas barras. */
function RecoverTrail({ second }: { second: boolean }) {
  const { t } = useLingui();
  return (
    <FlowPanelTrail
      steps={[
        { id: 'email', title: t`E-mail` },
        { id: 'password', title: t`Nova senha` },
      ]}
      current={second ? 'password' : 'email'}
      className="-mt-3"
    />
  );
}

function BackToLogin() {
  return (
    <p className="text-center">
      <Link to="/login" className={authLinkClass}>
        <ArrowLeft aria-hidden className="size-4" />
        <Trans>Voltar</Trans>
      </Link>
    </p>
  );
}

function EmailStep({ session, pending }: { session: AuthSession; pending: boolean }) {
  const { t } = useLingui();
  const form = useForm<z.infer<ReturnType<typeof recoverEmailSchema>>>({
    resolver: zodResolver(recoverEmailSchema()),
    defaultValues: { email: '' },
  });
  const onSubmit = form.handleSubmit(async ({ email }) => {
    await session.sendPasswordRecoveryCode(email);
  });
  return (
    <form className="uyzy-rise flex flex-col gap-7" noValidate onSubmit={(event) => void onSubmit(event)}>
      <AuthField
        label={t`E-mail`}
        type="email"
        inputMode="email"
        icon={Mail}
        autoComplete="username"
        registration={form.register('email')}
        error={form.formState.errors.email}
      />
      <AuthSubmit pending={pending}>
        <Trans>Enviar código</Trans>
      </AuthSubmit>
      <BackToLogin />
    </form>
  );
}

function ConfirmStep({ session, email, pending }: { session: AuthSession; email: string; pending: boolean }) {
  const { t } = useLingui();
  const follow = useAuthNavigation(session);
  const form = useForm<z.infer<ReturnType<typeof recoverConfirmSchema>>>({
    resolver: zodResolver(recoverConfirmSchema()),
    defaultValues: { code: '', newPassword: '', passwordConfirmation: '' },
  });
  const password = useWatch({ control: form.control, name: 'newPassword' });
  const onSubmit = form.handleSubmit(async ({ code, newPassword }) => {
    await follow(await session.confirmPasswordRecovery(email, code, newPassword));
  });
  return (
    <form className="uyzy-rise flex flex-col gap-7" noValidate onSubmit={(event) => void onSubmit(event)}>
      <p className="-mt-3 flex items-center gap-2 rounded-xl bg-card px-3.5 py-2.5 text-sm text-muted-foreground">
        <Mail aria-hidden className="size-4 shrink-0" strokeWidth={1.75} />
        <span className="min-w-0 truncate">
          <Trans>
            Enviado para <strong className="font-medium text-foreground">{email}</strong>
          </Trans>
        </span>
      </p>
      <div className="flex flex-col gap-4">
        <AuthField
          label={t`Código de confirmação`}
          icon={Hash}
          code
          inputMode="numeric"
          autoComplete="one-time-code"
          registration={form.register('code')}
          error={form.formState.errors.code}
        />
        <AuthPasswordField label={t`Nova senha`} autoComplete="new-password" registration={form.register('newPassword')} error={form.formState.errors.newPassword} />
        <PasswordChecklist value={password} />
        <AuthPasswordField
          label={t`Confirmar nova senha`}
          autoComplete="new-password"
          registration={form.register('passwordConfirmation')}
          error={form.formState.errors.passwordConfirmation}
        />
      </div>
      <AuthSubmit pending={pending}>
        <Trans>Confirmar</Trans>
      </AuthSubmit>
      <p className="text-center">
        <button type="button" className={authLinkClass} onClick={() => session.resetCodeSent()}>
          <ArrowLeft aria-hidden className="size-4" />
          <Trans>Voltar</Trans>
        </button>
      </p>
    </form>
  );
}
